import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Star, 
  ArrowLeft,
  Medal,
  Crown,
  RefreshCw,
  Home,
  Maximize2
} from 'lucide-react'
import { databaseService } from '@/services/database'
import { realtimeService } from '@/services/realtime'
import { Room, Player, Prompt, Vote } from '@/types'
import ReportContentButton from '@/components/ReportContentButton'
import FullscreenImageModal from '@/components/FullscreenImageModal'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@/services/analytics'

interface PromptWithStats {
  prompt: Prompt
  player: Player
  votes: Vote[]
  averageScore: number
  totalScore: number
}

interface PlayerStatsItem {
  player: Player
  totalScore: number
  voteCount: number
  promptCount: number
  averageScore: number
}

export default function ResultsPage() {
  const { roomId } = useParams<{ roomId: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  
  const [room, setRoom] = useState<Room | null>(null)
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null)
  const [promptsWithStats, setPromptsWithStats] = useState<PromptWithStats[]>([])
  const [playerStats, setPlayerStats] = useState<PlayerStatsItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showFullscreenImage, setShowFullscreenImage] = useState(false)
  const [selectedImageData, setSelectedImageData] = useState<{
    imageUrl: string
    promptText?: string
    playerName?: string
    roundNumber?: number
  } | null>(null)

  useEffect(() => {
    if (!roomId) {
      navigate('/')
      return
    }

    // Get current player from localStorage
    const savedPlayer = localStorage.getItem('currentPlayer')
    if (savedPlayer) {
      setCurrentPlayer(JSON.parse(savedPlayer))
    }

    loadResults()
  }, [roomId])

  const loadResults = async () => {
    try {
      const roomData = await databaseService.getRoom(roomId!)
      setRoom(roomData)

      // Load prompts and votes across ALL rounds
      const prompts = await databaseService.getAllRoundPrompts(roomId!)
      const votes = await databaseService.getAllRoundVotes(roomId!)

      // Calculate statistics for each prompt
      const promptStats: PromptWithStats[] = prompts
        .filter(p => p.imageGenerationStatus === 'completed' && p.imageUrl)
        .map(prompt => {
          const promptVotes = votes.filter(v => v.promptId === prompt.id)
          const totalScore = promptVotes.reduce((sum, vote) => sum + vote.score, 0)
          const averageScore = promptVotes.length > 0 ? totalScore / promptVotes.length : 0
          const player = roomData.players.find(p => p.id === prompt.playerId)!

          return {
            prompt,
            player,
            votes: promptVotes,
            averageScore,
            totalScore
          }
        })
        .sort((a, b) => b.totalScore - a.totalScore)

      setPromptsWithStats(promptStats)

      // Build player aggregated scores across all prompts
      const scoreMap: Record<string, { total: number; votes: number; prompts: number }> = {}
      roomData.players.forEach(p => { scoreMap[p.id] = { total: 0, votes: 0, prompts: 0 } })

      promptStats.forEach(ps => {
        const pid = ps.player.id
        if (!scoreMap[pid]) scoreMap[pid] = { total: 0, votes: 0, prompts: 0 }
        scoreMap[pid].total += ps.totalScore
        scoreMap[pid].votes += ps.votes.length
        scoreMap[pid].prompts += 1
      })

      const aggregated: PlayerStatsItem[] = roomData.players.map(p => {
        const s = scoreMap[p.id] || { total: 0, votes: 0, prompts: 0 }
        const avg = s.votes > 0 ? s.total / s.votes : 0
        return {
          player: p,
          totalScore: s.total,
          voteCount: s.votes,
          promptCount: s.prompts,
          averageScore: avg,
        }
      }).sort((a, b) => b.totalScore - a.totalScore)

      setPlayerStats(aggregated)

      // Analytics: sonuclar sayfasi goruntulendi (oyun tamamlandi)
      trackEvent({
        event: 'scraible_results_view',
        properties: {
          room_id: roomData.id,
          game_mode: roomData.gameMode,
          max_rounds: roomData.maxRounds,
          rounds_played: roomData.roundNumber,
          players_count: roomData.players.length,
        },
      })
    } catch (error: any) {
      setError(error.message || t('results.errorUnknown'))
    } finally {
      setLoading(false)
    }
  }

  const startNextRound = async () => {
    if (!room || !currentPlayer?.isHost) return

    try {
      // TODO: Implement next round logic
      // For now, just go back to lobby
      navigate(`/lobby/${roomId}`)
    } catch (error: any) {
      setError(error.message || 'Bir sonraki tur başlatılırken hata oluştu')
    }
  }


  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Crown className="w-6 h-6 text-yellow-500" />
      case 1:
        return <Medal className="w-6 h-6 text-gray-400" />
      case 2:
        return <Medal className="w-6 h-6 text-orange-600" />
      default:
        return <span className="w-6 h-6 flex items-center justify-center text-gray-500 font-bold">{index + 1}</span>
    }
  }

  // getRankStyle removed

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  if (error || !room) {
    return (
      <div className="text-center space-y-4">
        <div className="card max-w-md mx-auto">
          <h2 className="text-xl font-semibold text-red-600 mb-4">{t('results.errorTitle')}</h2>
          <p className="text-gray-600 mb-4">{error || t('results.errorUnknown')}</p>
          <button
            onClick={async () => {
              try {
                if (room && currentPlayer) {
                  await databaseService.leaveRoom(room.id, currentPlayer.id)
                  if (roomId) {
                    await realtimeService.broadcast(roomId, 'player_left', { playerId: currentPlayer.id })
                    // Clear session storage so player can rejoin properly
                    sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
                  }
                }
              } finally {
                navigate('/')
              }
            }}
            className="btn-primary"
          >
            {t('results.backHome')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative"
      >
        {/* Mobile: lobby (left), title (center), spacer (right) */}
        <div className="sm:hidden mb-3">
          <div className="flex items-center px-4">
            <div className="flex-shrink-0">
              <button
                onClick={() => navigate(`/lobby/${roomId}`)}
                className="btn-secondary flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('results.lobby')}</span>
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-xl font-bold text-gray-100">{t('results.header')}</h1>
                <p className="text-white/60 text-xs">{t('results.totalRounds', { count: room.maxRounds })}</p>
              </div>
            </div>
            <div className="flex-shrink-0 w-[88px]"></div>
          </div>
        </div>

        {/* Desktop header */}
        <div className="hidden sm:flex items-center justify-between">
          <button
            onClick={() => navigate(`/lobby/${roomId}`)}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('results.lobby')}</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-100">{t('results.header')}</h1>
            <p className="text-white/60 text-base">{t('results.totalRounds', { count: room.maxRounds })}</p>
          </div>
          
          <div className="w-20"></div>
        </div>
      </motion.div>

        {/* MVP Player - Professional Design */}
        {playerStats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="relative bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border border-amber-200/50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 rounded-2xl"></div>
            <div className="relative z-10">
              <div className="flex items-center justify-center space-x-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full shadow-lg">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-700 to-orange-600 bg-clip-text text-transparent">
                  {t('results.mvp')}
                </h2>
              </div>
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto shadow-lg ring-4 ring-white">
                  {playerStats[0].player.name.charAt(0).toUpperCase()}
                </div>
                <div className="text-2xl font-bold text-gray-800">{playerStats[0].player.name}</div>
                <div className="bg-white/60 backdrop-blur rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-center space-x-2 text-gray-700">
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      <span className="text-xl font-bold">{playerStats[0].totalScore}</span>
                    </div>
                    <span className="text-gray-600 font-medium">{t('results.totalPoints')}</span>
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {t('results.avg', { avg: playerStats[0].averageScore.toFixed(2), count: playerStats[0].promptCount })}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

      {/* Winner Announcement removed */}

        {/* Player Leaderboard - Professional Design */}
        {playerStats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white/80 backdrop-blur border border-gray-200/50 rounded-2xl p-6 shadow-lg"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-6 text-center">{t('results.playerRanking')}</h2>
            <div className="space-y-3">
              {playerStats.map((ps, index) => (
                <div 
                  key={ps.player.id} 
                  className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
                    index === 0 
                      ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200/50 shadow-sm' 
                      : index === 1
                      ? 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200/50'
                      : index === 2
                      ? 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200/50'
                      : 'bg-white/60 border-gray-200/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${
                        index === 0 
                          ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' 
                          : index === 1
                          ? 'bg-gradient-to-br from-gray-400 to-slate-500 text-white'
                          : index === 2
                          ? 'bg-gradient-to-br from-orange-400 to-red-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-gray-800">{ps.player.name}</span>
                          {ps.player.id === currentPlayer?.id && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                              {t('results.you')}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {t('results.imagesCount', { count: ps.promptCount })} • {t('results.avgShort', { avg: ps.averageScore.toFixed(2) })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      <span className="text-xl font-bold text-gray-800">{ps.totalScore}</span>
                      <span className="text-sm text-gray-600 font-medium">{t('results.points')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* All Results - Professional Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-6"
        >
          <h2 className="text-2xl font-bold text-center bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            {t('results.allResults')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {promptsWithStats.map((item, index) => (
              <motion.div
                key={item.prompt.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + (index % 6) * 0.05 }}
                className="group bg-white/80 backdrop-blur border border-gray-200/50 rounded-2xl p-4 shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all duration-300"
              >
                <div className="relative mb-4 overflow-hidden rounded-xl">
                  <img
                    src={item.prompt.imageUrl}
                    alt={`${item.player.name}'nin promptu`}
                    className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
                  <div className="absolute top-3 left-3">
                    <div className={`px-3 py-1.5 rounded-lg border backdrop-blur-sm shadow-sm flex items-center gap-2 text-sm font-semibold ${
                      index === 0 
                        ? 'bg-gradient-to-r from-yellow-400/90 to-amber-500/90 text-white border-yellow-300/50' 
                        : index === 1
                        ? 'bg-gradient-to-r from-gray-400/90 to-slate-500/90 text-white border-gray-300/50'
                        : index === 2
                        ? 'bg-gradient-to-r from-orange-400/90 to-red-500/90 text-white border-orange-300/50'
                        : 'bg-white/90 text-gray-700 border-gray-300/50'
                    }`}>
                      {getRankIcon(index)}
                      <span>#{index + 1}</span>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 flex space-x-2">
                    <ReportContentButton roomId={room.id} promptId={item.prompt.id} imageUrl={item.prompt.imageUrl || undefined} compact />
                    <motion.button
                      onClick={() => {
                        setSelectedImageData({
                          imageUrl: item.prompt.imageUrl || '',
                          promptText: item.prompt.text,
                          playerName: item.player.name,
                          roundNumber: item.prompt.roundNumber
                        })
                        setShowFullscreenImage(true)
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-10 h-10 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                      title="View fullscreen"
                    >
                      <Maximize2 className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 min-w-0">
                      <span className="font-bold text-gray-800 truncate">
                        {item.player.name}
                      </span>
                      {item.player.id === currentPlayer?.id && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                          {t('results.you')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <Star className="w-5 h-5 text-yellow-500 fill-current" />
                      <span className="text-lg font-bold text-gray-800">
                        {item.averageScore.toFixed(1)}
                      </span>
                      <span className="text-sm text-gray-500 font-medium">{t('results.votesCount', { count: item.votes.length })}</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">"{item.prompt.text}"</p>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    {[1, 2, 3, 4, 5].map(score => {
                      const count = item.votes.filter(v => v.score === score).length
                      return count > 0 ? (
                        <span key={score} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                          {t('results.breakdown', { score, count })}
                        </span>
                      ) : null
                    })}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="space-y-4"
      >
        {currentPlayer?.isHost && room.roundNumber < room.maxRounds && (
          <button
            onClick={startNextRound}
            className="w-full btn-primary flex items-center justify-center space-x-2"
          >
            <RefreshCw className="w-5 h-5" />
            <span>{t('results.nextRound', { current: room.roundNumber + 1, max: room.maxRounds })}</span>
          </button>
        )}

        {room.roundNumber >= room.maxRounds && (
          <div className="text-center space-y-4">
            <div className="card bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
              <h2 className="text-xl font-bold text-green-800 mb-2">{t('results.gameCompletedTitle')}</h2>
              <p className="text-green-700">{t('results.gameCompletedDesc')}</p>
            </div>
            

            <button
              onClick={async () => {
                try {
                  if (room && currentPlayer) {
                    await databaseService.leaveRoom(room.id, currentPlayer.id)
                    if (roomId) {
                      await realtimeService.broadcast(roomId, 'player_left', { playerId: currentPlayer.id })
                      // Clear session storage so player can rejoin properly
                      sessionStorage.removeItem(`joinedBroadcast:${roomId}`)
                    }
                  }
                } finally {
                  navigate('/')
                }
              }}
              className="btn-primary flex items-center justify-center space-x-2"
            >
              <Home className="w-5 h-5" />
              <span>{t('results.backHome')}</span>
            </button>
          </div>
        )}

        {!currentPlayer?.isHost && room.roundNumber < room.maxRounds && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t('results.waitingHostNext')}
            </p>
          </div>
        )}
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <p className="text-red-600 text-center">{error}</p>
        </motion.div>
      )}
      {/* Fullscreen Image Modal */}
      <FullscreenImageModal
        isOpen={showFullscreenImage}
        onClose={() => {
          setShowFullscreenImage(false)
          setSelectedImageData(null)
        }}
        imageUrl={selectedImageData?.imageUrl || ''}
        imageAlt={`Results image: ${selectedImageData?.playerName}'s creation`}
        promptText={selectedImageData?.promptText}
        playerName={selectedImageData?.playerName}
        roundNumber={selectedImageData?.roundNumber}
      />
    </div>
  )
}
